<template>
  <div class="filter-panel">
    <!-- Inline Display (Desktop) -->
    <div
      v-if="isInlineMode"
      class="filter-panel-inline"
    >
      <quick-filter-panel
        ref="filterPanel"
        :fields="normalizedFields"
        :values="internalValues"
        :title="title"
        :show-header="showHeader"
        :show-labels="showLabels"
        @update="handleFilterUpdate"
        @clear="handleClearFilters"
      >
        <!-- Pass through custom field slots -->
        <template
          v-for="field in fields"
          v-slot:[`field.${field.key}`]="slotProps"
        >
          <slot
            :name="`field.${field.key}`"
            v-bind="slotProps"
          />
        </template>
      </quick-filter-panel>

      <!-- Saved Filters (inline) -->
      <div
        v-if="showSavedFilters && viewName"
        class="saved-filters-inline mt-3"
      >
        <saved-filters-menu
          :view-name="viewName"
          @apply-filter="handleApplySavedFilter"
          @save-current="handleSaveCurrentFilter"
        />
      </div>
    </div>

    <!-- Drawer Trigger Button (Mobile/Tablet) -->
    <v-btn
      v-if="!isInlineMode && !drawerOpen"
      outlined
      class="text-none filter-panel-trigger"
      :class="{ 'filter-panel-trigger--active': hasActiveFilters }"
      @click="openDrawer"
    >
      <font-awesome-icon
        :icon="['fas', 'filter']"
        size="sm"
        :class="{ 'mr-1': $vuetify.breakpoint.smAndUp }"
      />
      <span class="d-none d-sm-inline">{{ triggerLabel }}</span>
      <v-badge
        v-if="activeFilterCount > 0"
        :content="String(activeFilterCount)"
        color="primary"
        inline
        class="ml-2"
      />
    </v-btn>

    <!-- Filter Drawer (Mobile/Tablet) -->
    <v-navigation-drawer
      v-if="!isInlineMode"
      v-model="drawerOpen"
      :width="drawerWidth"
      :bottom="isBottomDrawer"
      :right="!isBottomDrawer"
      temporary
      fixed
      class="filter-panel-drawer"
    >
      <!-- Drawer Header -->
      <v-toolbar
        flat
        dense
        color="white"
      >
        <font-awesome-icon
          :icon="['fas', 'filter']"
          class="primary--text mr-2"
          size="sm"
        />
        <v-toolbar-title class="text-subtitle-1 font-weight-medium">
          {{ title }}
        </v-toolbar-title>

        <v-spacer />

        <v-badge
          v-if="activeFilterCount > 0"
          :content="String(activeFilterCount)"
          color="primary"
          inline
          class="mr-2"
        />

        <v-btn
          icon
          small
          @click="closeDrawer"
        >
          <font-awesome-icon
            :icon="['fas', 'xmark']"
            size="lg"
          />
        </v-btn>
      </v-toolbar>

      <v-divider />

      <!-- Drawer Content -->
      <div class="filter-drawer-content pa-4">
        <quick-filter-panel
          ref="filterPanelDrawer"
          :fields="normalizedFields"
          :values="pendingValues"
          :show-header="false"
          :show-labels="true"
          @update="handlePendingFilterUpdate"
          @clear="handleClearPendingFilters"
        />

        <!-- Saved Filters (drawer) -->
        <div
          v-if="showSavedFilters && viewName"
          class="saved-filters-drawer mt-4"
        >
          <v-divider class="mb-4" />
          <saved-filters-menu
            :view-name="viewName"
            @apply-filter="handleApplySavedFilterDrawer"
            @save-current="handleSaveCurrentFilter"
          />
        </div>
      </div>

      <!-- Drawer Footer (Apply/Clear buttons) -->
      <template v-slot:append>
        <v-divider />

        <div class="pa-3 d-flex">
          <v-btn
            text
            class="text-none flex-grow-1 mr-2"
            :disabled="!hasPendingChanges && !hasActiveFilters"
            @click="handleClearAndClose"
          >
            <font-awesome-icon
              :icon="['fas', 'xmark']"
              size="sm"
              class="mr-1"
            />
            Clear
          </v-btn>

          <v-btn
            color="primary"
            class="text-none flex-grow-1"
            :disabled="!hasPendingChanges"
            @click="handleApplyAndClose"
          >
            <font-awesome-icon
              :icon="['fas', 'check']"
              size="sm"
              class="mr-1"
            />
            Apply
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>
  </div>
</template>

<script>
/**
 * FilterPanel Component
 *
 * A unified, responsive filter panel that provides consistent filtering UI across all views.
 * Supports both inline display (desktop) and drawer display (mobile/tablet).
 *
 * Features:
 * - Props-based field configuration (Vue 3 ready)
 * - Responsive display mode (inline on md+, drawer on sm/xs)
 * - Debounced text inputs for performance
 * - Saved filters integration via Firestore
 * - Component-level state management (no global store dependency)
 *
 * @example
 * <filter-panel
 *   v-model="filterValues"
 *   :fields="filterFields"
 *   view-name="pacing"
 *   title="Pacing Filters"
 * />
 */
import { debounce } from 'lodash-es'

export default {
  name: 'FilterPanel',

  components: {
    QuickFilterPanel: () => import('./QuickFilterPanel.vue'),
    SavedFiltersMenu: () => import('./SavedFiltersMenu.vue'),
  },

  model: {
    prop: 'value',
    event: 'input',
  },

  props: {
    /**
     * Current filter values object { fieldKey: value }
     * Use v-model for two-way binding
     */
    value: {
      type: Object,
      default: () => ({}),
    },

    /**
     * Array of filter field configurations
     * See docs/unified-filter-panel-design.md for field schema
     */
    fields: {
      type: Array,
      required: true,
      validator: (fields) => fields.every((f) => f.key && f.label),
    },

    /**
     * View identifier for saved filters (e.g., 'pacing', 'discrepancy', 'time')
     * Required for saved filters functionality
     */
    viewName: {
      type: String,
      default: '',
    },

    /**
     * Panel title
     */
    title: {
      type: String,
      default: 'Filters',
    },

    /**
     * Display mode
     * - 'auto': Inline on md+, drawer on smaller screens (default)
     * - 'inline': Always show inline
     * - 'drawer': Always use drawer
     */
    displayMode: {
      type: String,
      default: 'auto',
      validator: (v) => ['auto', 'inline', 'drawer'].includes(v),
    },

    /**
     * Apply mode for filter changes
     * - 'immediate': Apply filters on each change (good for small datasets)
     * - 'debounced': Debounce text inputs, immediate for selects (default)
     * - 'explicit': Require Apply button click (good for large datasets)
     */
    applyMode: {
      type: String,
      default: 'debounced',
      validator: (v) => ['immediate', 'debounced', 'explicit'].includes(v),
    },

    /**
     * Debounce delay for text inputs (ms)
     */
    debounceDelay: {
      type: Number,
      default: 300,
    },

    /**
     * Show saved filters functionality
     */
    showSavedFilters: {
      type: Boolean,
      default: true,
    },

    /**
     * Show header with title and clear button
     */
    showHeader: {
      type: Boolean,
      default: true,
    },

    /**
     * Show labels above each field
     */
    showLabels: {
      type: Boolean,
      default: false,
    },

    /**
     * Label for trigger button (mobile)
     */
    triggerLabel: {
      type: String,
      default: 'Filter',
    },
  },

  data() {
    return {
      drawerOpen: false,
      // Pending values for drawer mode (not applied until Apply button)
      pendingValues: {},
      // Debounced emit function
      debouncedEmit: null,
    }
  },

  computed: {
    /**
     * Whether to display inline (true) or as drawer trigger (false)
     */
    isInlineMode() {
      if (this.displayMode === 'inline') return true
      if (this.displayMode === 'drawer') return false
      // Auto mode: inline on md+
      return this.$vuetify.breakpoint.mdAndUp
    },

    /**
     * Use bottom drawer on xs screens, right drawer otherwise
     */
    isBottomDrawer() {
      return this.$vuetify.breakpoint.xsOnly
    },

    /**
     * Drawer width based on screen size
     */
    drawerWidth() {
      if (this.isBottomDrawer) return '100%'
      return 360
    },

    /**
     * Internal values for inline mode
     * Uses prop value directly for immediate/debounced modes
     */
    internalValues() {
      return this.value || {}
    },

    /**
     * Normalize fields to ensure consistent structure
     * Adds defaults for missing properties
     */
    normalizedFields() {
      return this.fields.map((field) => ({
        // Defaults
        type: 'select',
        multiple: false,
        searchable: false,
        clearable: true,
        items: [],
        itemText: 'text',
        itemValue: 'value',
        // User overrides
        ...field,
      }))
    },

    /**
     * Count of fields with active filter values
     */
    activeFilterCount() {
      return this.fields.filter((field) => {
        const value = this.value?.[field.key]
        if (Array.isArray(value)) return value.length > 0
        return value !== null && value !== undefined && value !== ''
      }).length
    },

    /**
     * Whether any filters are currently active
     */
    hasActiveFilters() {
      return this.activeFilterCount > 0
    },

    /**
     * Whether pending values differ from current values
     */
    hasPendingChanges() {
      const pending = this.pendingValues || {}
      const current = this.value || {}

      return this.fields.some((field) => {
        const pendingVal = pending[field.key]
        const currentVal = current[field.key]

        if (Array.isArray(pendingVal) && Array.isArray(currentVal)) {
          if (pendingVal.length !== currentVal.length) return true
          return pendingVal.some((v, i) => v !== currentVal[i])
        }

        return pendingVal !== currentVal
      })
    },
  },

  watch: {
    /**
     * Sync pending values when prop value changes externally
     */
    value: {
      immediate: true,
      deep: true,
      handler(newValue) {
        this.pendingValues = { ...newValue }
      },
    },
  },

  created() {
    // Create debounced emit function
    this.debouncedEmit = debounce((values) => {
      this.$emit('input', values)
    }, this.debounceDelay)
  },

  beforeDestroy() {
    // Cancel any pending debounced calls
    if (this.debouncedEmit?.cancel) {
      this.debouncedEmit.cancel()
    }
  },

  methods: {
    /**
     * Open the filter drawer
     */
    openDrawer() {
      this.pendingValues = { ...this.value }
      this.drawerOpen = true
      this.$emit('drawer-change', true)
    },

    /**
     * Close the filter drawer
     */
    closeDrawer() {
      this.drawerOpen = false
      this.$emit('drawer-change', false)
    },

    /**
     * Handle filter update from inline QuickFilterPanel
     */
    handleFilterUpdate(values) {
      if (this.applyMode === 'immediate') {
        this.$emit('input', values)
      } else if (this.applyMode === 'debounced') {
        // Check if the changed field is a text field
        const changedKey = this.findChangedKey(this.value, values)
        const changedField = this.fields.find((f) => f.key === changedKey)

        if (changedField?.type === 'text' || changedField?.type === 'number') {
          // Debounce text/number inputs
          this.debouncedEmit(values)
        } else {
          // Immediate for dropdowns, checkboxes, etc.
          this.$emit('input', values)
        }
      } else {
        // Explicit mode - update pending but don't emit
        this.pendingValues = values
      }
    },

    /**
     * Handle filter update in drawer (always updates pending)
     */
    handlePendingFilterUpdate(values) {
      this.pendingValues = values
    },

    /**
     * Find which key changed between two value objects
     */
    findChangedKey(oldValues, newValues) {
      const changedField = this.fields.find((f) => {
        const oldVal = oldValues?.[f.key]
        const newVal = newValues?.[f.key]
        return JSON.stringify(oldVal) !== JSON.stringify(newVal)
      })
      return changedField?.key || null
    },

    /**
     * Clear all filters (inline mode)
     */
    handleClearFilters() {
      const clearedValues = {}
      this.fields.forEach((field) => {
        clearedValues[field.key] = field.multiple ? [] : null
      })
      this.$emit('input', clearedValues)
      this.$emit('clear')
    },

    /**
     * Clear pending filters (drawer mode)
     */
    handleClearPendingFilters() {
      const clearedValues = {}
      this.fields.forEach((field) => {
        clearedValues[field.key] = field.multiple ? [] : null
      })
      this.pendingValues = clearedValues
    },

    /**
     * Clear filters and close drawer
     */
    handleClearAndClose() {
      this.handleClearFilters()
      this.closeDrawer()
    },

    /**
     * Apply pending filters and close drawer
     */
    handleApplyAndClose() {
      this.$emit('input', { ...this.pendingValues })
      this.$emit('apply', { ...this.pendingValues })
      this.closeDrawer()
    },

    /**
     * Apply a saved filter (inline mode)
     */
    handleApplySavedFilter(savedFilter) {
      if (savedFilter?.filters) {
        this.$emit('input', { ...savedFilter.filters })
      }
    },

    /**
     * Apply a saved filter (drawer mode)
     */
    handleApplySavedFilterDrawer(savedFilter) {
      if (savedFilter?.filters) {
        this.pendingValues = { ...savedFilter.filters }
      }
    },

    /**
     * Handle save current filter request
     */
    handleSaveCurrentFilter() {
      const currentValues = this.isInlineMode ? this.value : this.pendingValues
      this.$emit('save-filter', { values: currentValues })
    },

    // Public methods for parent access via ref

    /**
     * Get current filter values
     */
    getFilters() {
      return { ...this.value }
    },

    /**
     * Reset all filters
     */
    resetFilters() {
      this.handleClearFilters()
    },

    /**
     * Programmatically open the drawer
     */
    open() {
      if (!this.isInlineMode) {
        this.openDrawer()
      }
    },

    /**
     * Programmatically close the drawer
     */
    close() {
      if (!this.isInlineMode) {
        this.closeDrawer()
      }
    },
  },
}
</script>

<style scoped lang="scss">
.filter-panel {
  // Inline mode container
  .filter-panel-inline {
    width: 100%;
  }

  // Trigger button active state
  .filter-panel-trigger--active {
    border-color: var(--v-primary-base);
    color: var(--v-primary-base);
  }
}

// Drawer content scrollable area (standalone for proper specificity ordering)
.filter-drawer-content {
  overflow-y: auto;
  height: calc(100% - 48px - 64px); // Subtract toolbar and footer heights
}

// Scrollbar pseudo-elements (flattened for proper specificity)
.filter-drawer-content::-webkit-scrollbar {
  width: 6px;
}

.filter-drawer-content::-webkit-scrollbar-track {
  background: transparent;
}

.filter-drawer-content::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.filter-drawer-content::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

// Drawer styles
.filter-panel-drawer {
  // Theme support
  &.theme--dark {
    background-color: #1e1e1e;

    .v-toolbar {
      background-color: #1e1e1e !important;
    }
  }
}

// Dark mode scrollbar
.theme--dark .filter-drawer-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
}

.theme--dark .filter-drawer-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>
